import { IMinUserDepricated, IValueLabel } from "@mctypes/global.types";
import { IRegister } from "@mctypes/register.types";
import { ActionIcon, Button, Group, Paper, Select, Stack, Text, TextInput, Title, Tooltip } from "@mantine/core";
import { IconCheck, IconPencil, IconX } from "@tabler/icons-react";
import React, { useState } from "react";
import CheckOrWarn from "../CheckOrWarn";

const GeneralSettingsCard: React.FC<{
  board: IRegister.BoardPopulated;
  saveCb: <K extends keyof IRegister.BoardPopulated, V>(
    board: IRegister.BoardPopulated,
    prop: K,
    oldValue: V,
    newValue: V,
  ) => void;
  allUsers: IMinUserDepricated[];
  statuses: IValueLabel[];
  hideAccess?: boolean;
  useIsValid: boolean;
  isValid?: boolean;
  canStart?: boolean;
  startNow?: () => void;
}> = ({ board, saveCb, allUsers, statuses, isValid, canStart, startNow, hideAccess = false, useIsValid }) => {
  const [nameEditMode, setNameEditMode] = useState(false);
  const [nameEditValue, setNameEditValue] = useState(board.name);

  const [statusEditMode, setStatusEditMode] = useState(false);
  const [statusEditValue, setStatusEditValue] = useState(board.status);

  const saveName = () => {
    if (nameEditValue !== board.name) {
      saveCb(board, "name", board.name, nameEditValue);
    }
    setNameEditMode(false);
  };

  const cancelName = () => {
    setNameEditValue(board.name);
    setNameEditMode(false);
  };

  const saveStatus = () => {
    if (statusEditValue !== board.status) {
      saveCb(board, "status", board.status, statusEditValue);
    }
    setStatusEditMode(false);
  };

  const cancelStatus = () => {
    setStatusEditValue(board.status);
    setStatusEditMode(false);
  };

  const currentStatusLabel = statuses.find((s) => s.value === board.status)?.label ?? board.status;

  return (
    <Paper withBorder radius="md" p="lg" shadow="xs">
      <Stack gap="sm">
        <Group gap="xs" align="center">
          {useIsValid === true && <CheckOrWarn bool={isValid} />}
          <Title order={3}>Generelt</Title>
        </Group>
        <Text size="sm" c="dimmed">
          Oppdater navn og status for denne enheten.
        </Text>

        {/* Name */}
        <Stack gap={4}>
          <Text size="sm" fw={600}>
            Navn
          </Text>
          {nameEditMode ? (
            <Group gap="xs" align="center" wrap="nowrap">
              <TextInput
                size="sm"
                value={nameEditValue}
                onChange={(e) => setNameEditValue(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") saveName();
                  if (e.key === "Escape") cancelName();
                }}
                autoFocus
              />
              <Tooltip label="Lagre">
                <ActionIcon
                  variant="light"
                  size="sm"
                  disabled={nameEditValue === board.name || nameEditValue.trim() === ""}
                  onClick={saveName}
                >
                  <IconCheck size={15} />
                </ActionIcon>
              </Tooltip>
              <Tooltip label="Avbryt">
                <ActionIcon variant="subtle" color="warmgray.6" size="sm" onClick={cancelName}>
                  <IconX size={15} />
                </ActionIcon>
              </Tooltip>
            </Group>
          ) : (
            <Group gap="xs" align="center">
              <Text size="sm">{board.name || "–"}</Text>
              <Tooltip label="Rediger">
                <ActionIcon variant="subtle" color="mc-primary" size="sm" onClick={() => setNameEditMode(true)}>
                  <IconPencil size={14} />
                </ActionIcon>
              </Tooltip>
            </Group>
          )}
        </Stack>

        {/* Status */}
        <Stack gap={4}>
          <Text size="sm" fw={600}>
            Status
          </Text>
          {statusEditMode ? (
            <Group gap="xs" align="center" wrap="nowrap">
              <Select
                size="sm"
                value={statusEditValue}
                data={statuses.map((s) => ({ value: s.value, label: s.label }))}
                onChange={(v) => v != null && setStatusEditValue(v)}
                allowDeselect={false}
                style={{ width: "fit-content", minWidth: 160 }}
                comboboxProps={{ withinPortal: true }}
              />
              <Tooltip label="Lagre">
                <ActionIcon variant="light" size="sm" disabled={statusEditValue === board.status} onClick={saveStatus}>
                  <IconCheck size={15} />
                </ActionIcon>
              </Tooltip>
              <Tooltip label="Avbryt">
                <ActionIcon variant="subtle" color="warmgray.6" size="sm" onClick={cancelStatus}>
                  <IconX size={15} />
                </ActionIcon>
              </Tooltip>
            </Group>
          ) : (
            <Group gap="xs" align="center">
              <Text size="sm">{currentStatusLabel || "–"}</Text>
              <Tooltip label="Rediger">
                <ActionIcon variant="subtle" color="mc-primary" size="sm" onClick={() => setStatusEditMode(true)}>
                  <IconPencil size={14} />
                </ActionIcon>
              </Tooltip>
            </Group>
          )}
        </Stack>

        {startNow != null && (
          <Group justify="flex-start">
            <Button disabled={canStart} onClick={startNow}>
              Start nå
            </Button>
          </Group>
        )}
        {/*{hideAccess !== true && (
          <AccessHandlerEdit
            access={board.access}
            updateAccess={(oldValue, newValue) => saveCb(board, "access", oldValue, newValue)}
            allUsers={allUsers}
            createdByUserId={board.createdBy._id}
          />
        )}*/}
      </Stack>
    </Paper>
  );
};

export default GeneralSettingsCard;
